<template>
	<div
		v-if="images && images.length"
		:id="`block-${$vnode.key}`"
		class="c-block-list-images"
		:class="[
			{
				'c-block-list-images--wide': fullWidthView,
				'c-block-list-images--in-accordion': accordionLevel,
			},
			'c-block-list-images--count-' +
				(images.length > 2 ? 'multiple' : images.length),
		]"
	>
		<!-- Single image -->
		<template v-if="images.length === 1">
			<LongReadTarget
				v-if="title"
				class="relative mb-2xs/v z-10"
				:container-id="`block-${$vnode.key}`"
				:link-to-toc="{
					name: `Spring til &quot;${title}&quot; i indholdsfortegnelsen`,
					attrs: {
						'data-focus-ignore': true,
					},
				}"
				:title="title"
				:disabled="!showTitleInToc"
			>
				<BaseH2 :id="`block-${$vnode.key}__title`" v-html="title" />
			</LongReadTarget>
			<BaseImg
				:credits-id="`block-${$vnode.key}__credits`"
				class="max-w-full"
				:src="images[0].cropUrl || images[0].url"
				:alt="images[0].altText"
				:sizes="
					nuxtImgSizes(
						fullWidthView
							? '<1400:100vw <1441:1296px'
							: '<1024:100vw <1400:60vw <1441:864px'
					)
				"
				:width="images[0].width"
				:height="images[0].height"
				:credits="images[0].credits ? images[0].credits : null"
				:img-attrs="{
					'aria-describedby':
						[
							images[0].credits
								? `block-${$vnode.key}__credits`
								: null,
							description
								? `block-${$vnode.key}__description`
								: null,
						]
							.filter(Boolean)
							.join(' ') || null,
				}"
				loading="lazy"
			/>
		</template>

		<!-- Multiple images -->
		<SwiperWrap
			v-else
			:id="`block-${$vnode.key}__swiper`"
			ref="swiper"
			:move-by="1"
			class="c-block-list-images__swiper overflow-hidden"
			:class="{
				'>=1024:ml-0': !fullWidthView,
			}"
			:items-wrapper-class-names="[
				'flex w-full gap-4xs/h >=1024:gap-3xs/h pl-layout-margin overflow-x-auto overflow-y-hidden',
				'snap snap-x snap-mandatory',
				{
					'>=1024:overflow-x-hidden': images.length === 2,
					'>=1024:pl-0': !fullWidthView,
				},
			]"
			aria-label="Billedebånd"
		>
			<template #before="{ state, showPagination, go }">
				<header
					v-if="title || state.scrollable"
					class="
						c-block-list-images__header
						w-full
						flex
						gap-4xs/h
						justify-between
						items-end
					"
				>
					<LongReadTarget
						v-if="title"
						class="relative mb-2xs/v flex-shrink flex-grow z-10"
						:container-id="`block-${$vnode.key}`"
						:link-to-toc="{
							name: `Spring til &quot;${title}&quot; i indholdsfortegnelsen`,
							attrs: {
								'data-focus-ignore': true,
							},
						}"
						:title="title"
						:disabled="!showTitleInToc"
					>
						<BaseH2
							:id="`block-${$vnode.key}__title`"
							v-html="title"
						/>
					</LongReadTarget>
					<span v-else></span>

					<nav
						v-if="state.scrollable"
						class="
							hidden
							justify-end
							flex-shrink-0
							gap-4xs/h
							mb-2xs/v
						"
						:class="{
							'>=768:flex': images.length > 2,
						}"
						aria-label="Billedenavigation"
					>
						<BaseButton
							class="
								w-40
								h-40
								p-0
								transition
								flex-shrink-0
								bg-white
								hover:bg-theme-10
								border border-current
								text-theme
							"
							:aria-disabled="
								showPagination.previous ? null : 'true'
							"
							aria-label="Tidligere billede"
							:aria-controls="`block-${$vnode.key}__swiper`"
							@click="() => go.previous(false)"
						>
							<SvgIconWedge
								class="
									w-12
									h-8
									transform
									rotate-90
									-translate-x-1
								"
							/>
						</BaseButton>

						<BaseButton
							class="
								w-40
								h-40
								p-0
								transition
								flex-shrink-0
								bg-white
								hover:bg-theme-10
								border border-current
								text-theme
							"
							:aria-disabled="showPagination.next ? null : 'true'"
							aria-label="Næste billede"
							:aria-controls="`block-${$vnode.key}__swiper`"
							@click="() => go.next(false)"
						>
							<SvgIconWedge
								class="
									w-12
									h-8
									transform
									-rotate-90
									translate-x-1
								"
							/>
						</BaseButton>
					</nav>
				</header>
			</template>

			<template #items>
				<div
					v-for="(image, index) in images"
					:key="index"
					class="
						c-block-list-images__item
						flex-grow flex-shrink-0
						snap-start
					"
					:class="{
						'flex-shrink-0 >=1024:flex-shrink': images.length === 2,
					}"
					:style="`--image-width: ${imageGetWidth(
						image
					)}%; --image-max-width: max(${imageGetMaxWidth(image)}px, ${
						(imageGetMaxWidth(image) / 500) * 75
					}vh); aspect-ratio: ${image.width} / ${image.height};`"
				>
					<BaseImg
						class="w-full h-full"
						wrapper-class-names="w-full h-full"
						:credits-id="`block-${$vnode.key}__credits-${index}`"
						:src="image.cropUrl || image.url"
						:alt="image.altText"
						:sizes="
							nuxtImgSizes(
								fullWidthView
									? '<1400:90vw <1441:1296px'
									: '<1024:90vw <1400:60vw <1441:864px'
							)
						"
						:width="image.width"
						:height="image.height"
						:credits="image.credits ? image.credits : null"
						:img-attrs="{
							'aria-describedby':
								[
									image.credits
										? `block-${$vnode.key}__credits-${index}`
										: null,
									description
										? `block-${$vnode.key}__description`
										: null,
								]
									.filter(Boolean)
									.join(' ') || null,
							fetchpriority: index > 2 ? 'low' : null,
						}"
						fit="cover"
						loading="lazy"
					/>
				</div>
				<div
					key="showstopper"
					class="
						c-block-list-images__item-showstopper
						flex-shrink-0 flex-grow-0
					"
					data-swiper-ignore
				></div>
			</template>
		</SwiperWrap>

		<div
			v-if="description"
			:id="`block-${$vnode.key}__description`"
			:class="[
				'mt-4xs/v >=768:mt-2xs/v',
				'text-gray-darker text-body-xs whitespace-pre-wrap',
			]"
			v-html="description"
		></div>
	</div>
</template>

<script>
import { LongReadTarget } from '~/components/shared/LongRead';
import SwiperWrap from '~/components/shared/SwiperWrap';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';
import { nuxtImgSizes } from '~/assets/js/utilities/helpers';

export default {
	name: 'BlockListImages',

	components: { LongReadTarget, SwiperWrap, SvgIconWedge },

	inject: {
		accordionLevel: {
			default: 0,
		},
		accordionParentPanel: {
			default: null,
		},
	},

	inheritAttrs: false,

	props: {
		title: String,
		showTitleInToc: Boolean,
		images: {
			type: Array,
			default: () => [],
		},
		description: String,

		fullWidthView: Boolean,
	},

	computed: {
		inOpenAccordion() {
			return !!this.accordionParentPanel?.isOpen;
		},
		factorList() {
			return this.images.map((image) => {
				return image.width / image.height;
			});
		},
		factorSum() {
			let factorSum =
				(this.factorList.reduce((a, b) => a + b, 0) /
					this.factorList.length) *
				2;
			if (this.images.length > 2) {
				factorSum *= 1.1;
			}
			return factorSum;
		},
	},

	watch: {
		inOpenAccordion(val) {
			val && this.$refs.swiper?.initElements?.();
		},
	},

	methods: {
		nuxtImgSizes,
		imageGetWidth(image) {
			const factor = image.width / image.height;
			return (
				Math.round((factor / Math.min(this.factorSum, 2.5)) * 9999) /
				100
			);
		},
		imageGetMaxWidth(image) {
			const minIndex = this.images
				.map((image) => image.height)
				.indexOf(Math.min(...this.images.map((image) => image.height)));
			const factor = image.width / image.height;
			return (
				Math.round(
					Math.min(
						(factor / this.factorSum) * 800,
						this.images[minIndex].width,
						(this.images[minIndex].width /
							this.images[minIndex].height) *
							500
					) * 100
				) / 100
			);
		},
	},
};
</script>

<style lang="postcss">
.c-block-list-images--wide {
	@apply w-12/12col justify-self-end;
}

/* Slim to small screens, always full width */
.c-block-list-images__header {
	@apply px-layout-margin;
}

.c-block-list-images__swiper {
	width: calc(
		var(--theme-layout-margin, var(--theme-layout-margin--sm)) * 2 + 100%
	);
	margin-left: calc(
		0px - var(--theme-layout-margin, var(--theme-layout-margin--sm))
	);

	& .c-swiper-wrap__content {
		overscroll-behavior-x: none;
	}
}

.c-block-list-images__item {
	width: calc(
		var(--image-width, 50%) * 1.3 -
			var(
				--theme-horizontalSpacing-3xs-h,
				var(--theme-horizontalSpacing-3xs-h--sm)
			) / 2
	);
	max-width: min(var(--image-max-width, 90%), 90%);
	scroll-margin-left: var(
		--theme-layout-margin,
		var(--theme-layout-margin--sm)
	);
}

.c-block-list-images__item-showstopper {
	width: calc(
		var(--theme-layout-margin, var(--theme-layout-margin--sm)) -
			var(
				--theme-horizontalSpacing-4xs-h,
				var(--theme-horizontalSpacing-4xs-h--sm)
			)
	);
}

@screen >=768 {
}

/* From there and up to max size, as per desktop design */
@screen >=1024 {
	.c-block-list-images__header {
		@apply pl-0;
	}
	.c-block-list-images--wide .c-block-list-images__header {
		@apply px-layout-margin;
	}
	.c-block-list-images--in-accordion .c-block-list-images__header {
		padding-inline: calc(
			var(
					--theme-horizontalSpacing-sm-h,
					var(--theme-horizontalSpacing-sm-h--sm)
				) +
				var(
					--theme-horizontalSpacing-2xs-h,
					var(--theme-horizontalSpacing-2xs-h--sm)
				)
		);
	}
	.c-block-list-images__swiper {
		width: calc(
			var(--theme-layout-margin, var(--theme-layout-margin--sm)) + 100%
		);
		margin-left: calc(
			0px - var(--theme-layout-margin, var(--theme-layout-margin--sm))
		);
	}
	.c-block-list-images--wide .c-block-list-images__swiper {
		width: calc(
			var(--theme-layout-margin, var(--theme-layout-margin--sm)) * 2 +
				100%
		);
	}
	.c-block-list-images--in-accordion .c-block-list-images__swiper {
		width: calc(
			100% +
				var(
					--theme-horizontalSpacing-sm-h,
					var(--theme-horizontalSpacing-sm-h--sm)
				) * 2 +
				var(
					--theme-horizontalSpacing-2xs-h,
					var(--theme-horizontalSpacing-2xs-h--sm)
				) * 2
		);
		margin-left: calc(
			0px -
				var(
					--theme-horizontalSpacing-sm-h,
					var(--theme-horizontalSpacing-sm-h--sm)
				) -
				var(
					--theme-horizontalSpacing-2xs-h,
					var(--theme-horizontalSpacing-2xs-h--sm)
				)
		) !important;

		& .c-swiper-wrap__content {
			padding-left: calc(
				var(
						--theme-horizontalSpacing-sm-h,
						var(--theme-horizontalSpacing-sm-h--sm)
					) +
					var(
						--theme-horizontalSpacing-2xs-h,
						var(--theme-horizontalSpacing-2xs-h--sm)
					)
			) !important;
		}
	}

	.c-block-list-images__item {
		width: calc(
			var(--image-width, 50%) -
				var(
					--theme-horizontalSpacing-3xs-h,
					var(--theme-horizontalSpacing-3xs-h--sm)
				) / 2
		);
		max-width: min(var(--image-max-width, 90%), 90%);
		scroll-margin-left: 0px;
	}
	.c-block-list-images--wide .c-block-list-images__item {
		scroll-margin-left: var(
			--theme-layout-margin,
			var(--theme-layout-margin--sm)
		);
	}
	.c-block-list-images--count-2 .c-block-list-images__item {
		max-width: 100%;
	}
	.c-block-list-images--in-accordion .c-block-list-images__item {
		scroll-margin-left: calc(
			var(
					--theme-horizontalSpacing-sm-h,
					var(--theme-horizontalSpacing-sm-h--sm)
				) +
				var(
					--theme-horizontalSpacing-2xs-h,
					var(--theme-horizontalSpacing-2xs-h--sm)
				)
		);
	}

	.c-block-list-images__item-showstopper {
		width: calc(
			var(--theme-layout-margin, var(--theme-layout-margin--sm)) -
				var(
					--theme-horizontalSpacing-3xs-h,
					var(--theme-horizontalSpacing-3xs-h--sm)
				)
		);
	}
	.c-block-list-images--in-accordion .c-block-list-images__item-showstopper {
		width: calc(
			var(
					--theme-horizontalSpacing-sm-h,
					var(--theme-horizontalSpacing-sm-h--sm)
				) +
				var(
					--theme-horizontalSpacing-2xs-h,
					var(--theme-horizontalSpacing-2xs-h--sm)
				) -
				var(
					--theme-horizontalSpacing-3xs-h,
					var(--theme-horizontalSpacing-3xs-h--sm)
				)
		);
	}
}

/* For large screens, go to bounds again but keep layout */
@screen >=1441 {
	.c-block-list-images__header {
		padding-right: calc(
			(
					var(--visual-viewport-width, 100vw) -
						var(--theme-layout-max, 1440px)
				) / 2 +
				var(--theme-layout-margin, var(--theme-layout-margin--sm))
		);
	}
	.c-block-list-images--count-multiple.c-block-list-images--wide
		.c-block-list-images__header {
		padding-inline: calc(
			(
					var(--visual-viewport-width, 100vw) -
						var(--theme-layout-max, 1440px)
				) / 2 +
				var(--theme-layout-margin, var(--theme-layout-margin--sm))
		);
	}
	.c-block-list-images--count-multiple:not(.c-block-list-images--in-accordion) {
		& .c-block-list-images__swiper {
			width: calc(
				100% +
					(
						var(--visual-viewport-width, 100vw) -
							var(--theme-layout-max, 1440px)
					) / 2 +
					var(--theme-layout-margin, var(--theme-layout-margin--sm))
			);
		}

		& .c-block-list-images__item-showstopper {
			width: calc(
				var(
						--theme-horizontalSpacing-sm-h,
						var(--theme-horizontalSpacing-sm-h--sm)
					) +
					var(
						--theme-horizontalSpacing-2xs-h,
						var(--theme-horizontalSpacing-2xs-h--sm)
					) -
					var(
						--theme-horizontalSpacing-3xs-h,
						var(--theme-horizontalSpacing-3xs-h--sm)
					) +
					(
						var(--visual-viewport-width, 100vw) -
							var(--theme-layout-max, 1440px)
					) / 2
			);
		}

		&.c-block-list-images--wide {
			& .c-block-list-images__swiper {
				width: var(--visual-viewport-width, 100vw);
				margin-left: calc(
					(100% - var(--visual-viewport-width, 100vw)) / 2
				) !important;

				& .c-swiper-wrap__content {
					padding-left: calc(
						(
								var(--visual-viewport-width, 100vw) -
									var(--theme-layout-max, 1440px)
							) / 2 +
							var(
								--theme-layout-margin,
								var(--theme-layout-margin--sm)
							)
					) !important;
				}
			}

			& .c-block-list-images__item {
				scroll-margin-left: calc(
					(
							var(--visual-viewport-width, 100vw) -
								var(--theme-layout-max, 1440px)
						) / 2 +
						var(
							--theme-layout-margin,
							var(--theme-layout-margin--sm)
						)
				);
			}
		}
	}
}
</style>
